import { WORDY_ACCEPTED_SET } from './data';

export type LetterState = 'absent' | 'present' | 'correct';

export function normalizeWord(value: string): string | null {
  const normalized = value.trim().toLowerCase();
  return /^[a-z]{5}$/.test(normalized) ? normalized : null;
}

export function isAcceptedGuess(value: string): boolean {
  const normalized = normalizeWord(value);
  return normalized !== null && WORDY_ACCEPTED_SET.has(normalized);
}

export function evaluateGuess(answerValue: string, guessValue: string): LetterState[] {
  const answer = normalizeWord(answerValue);
  const guess = normalizeWord(guessValue);
  if (!answer || !guess) throw new Error('Wordy evaluation requires two five-letter words.');

  const states: LetterState[] = Array.from({ length: 5 }, () => 'absent');
  const remaining = new Map<string, number>();

  for (let index = 0; index < answer.length; index += 1) {
    if (guess[index] === answer[index]) {
      states[index] = 'correct';
    } else {
      remaining.set(answer[index], (remaining.get(answer[index]) ?? 0) + 1);
    }
  }

  for (let index = 0; index < guess.length; index += 1) {
    if (states[index] === 'correct') continue;
    const count = remaining.get(guess[index]) ?? 0;
    if (count > 0) {
      states[index] = 'present';
      remaining.set(guess[index], count - 1);
    }
  }

  return states;
}

const stateRank: Record<LetterState, number> = { absent: 0, present: 1, correct: 2 };

export function buildKeyboardState(
  guesses: readonly string[],
  answer: string,
): Partial<Record<string, LetterState>> {
  const keyboard: Partial<Record<string, LetterState>> = {};
  for (const guess of guesses) {
    const states = evaluateGuess(answer, guess);
    guess.split('').forEach((letter, index) => {
      const next = states[index];
      const current = keyboard[letter];
      if (!current || stateRank[next] > stateRank[current]) keyboard[letter] = next;
    });
  }
  return keyboard;
}

export function getWordyScore(guessCount: number, solved: boolean): number {
  if (!solved) return 60_000;
  if (guessCount < 1 || guessCount > 6) throw new Error('Solved Wordy count must be from 1 to 6.');
  if (guessCount <= 3) return -15_000;
  if (guessCount === 4) return -5_000;
  if (guessCount === 5) return 5_000;
  return 15_000;
}
